import React from "react";
import { SELECTED_COUNTRY_KEY } from "../CountryContext";
import Badge from "../ui-components/Badge";
import { Link } from "react-router-dom";
import {
  useRoleNames,
  useGroupNames,
} from "../ui-components/pages/Roles/Hooks/useRoleGroup";
import Spinner from "../ui-components/Spinner";
import {
  SelectRoleNames,
  SelectGroupNames,
} from "../ui-components/pages/Roles/SelectRoleGroupNames";
import ExplorerPublicLink from "@components/ExplorerPublicLink";
import { UploadPublicLink, GetDeletePublicLink } from "@utils/PublicLinkUtils";
import {tableIcons} from "@ui-components/TableIcons";

export const SERVICE_TYPES_LOOKUP = {
  configurator: "Cantiere",
  tintometer: "Tintometro",
};

export const SERVICE_CLASS_LOOKUP = {
  0: "Progettazione",
  1: "Quotazione",
  2: "Servizio",
};

const UpdateRoleReadPerm = ({ value, onChange, rowData }) => {
  const roleNames = useRoleNames();

  if (!roleNames) {
    return (
      <div className="flex w-full justify-center">
        <Spinner />
      </div>
    );
  }

  return rowData.type === "tintometer" ? (
    <div>public</div>
  ) : (
    <SelectRoleNames
      roleNames={roleNames}
      scopes={value || []}
      setScopes={(e) => onChange(e)}
    />
  );
};

const UpdateGroupReadPerm = ({ value, onChange }) => {
  const groupNames = useGroupNames();

  if (!groupNames) {
    return (
      <div className="flex w-full justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <SelectGroupNames
      groupNames={groupNames}
      scopes={value || []}
      setScopes={(e) => onChange(e)}
    />
  );
};

const id = { title: "ID", field: "id", auto: true };

const explorerTables = [
  {
    path: "services",
    name: "Services",
    columns: [
      {
        title: "Order",
        field: "order",
        type: "number",
        required: true,
        defaultSort: "asc",
      },
      {
        title: "Name",
        field: "name",
        type: "string",
        tableType: "string",
        required: true,
        headerStyle: { minWidth: 220 },
        cellStyle: { minWidth: 220 },
      },
      {
        title: "Description",
        field: "description",
        type: "string",
        tableType: "string",
        headerStyle: { minWidth: 300 },
        cellStyle: { minWidth: 300 },
      },
      {
        title: "Type",
        field: "type",
        type: "select",
        lookup: SERVICE_TYPES_LOOKUP,
        required: true,
      },
      {
        title: "Class",
        field: "class",
        type: "select",
        lookup: SERVICE_CLASS_LOOKUP,
        required: true,
      },
      {
        title: "Read Roles",
        field: "r_perm",
        type: "string_array",
        tableType: "string",
        required: true,
        render: ({ r_perm }) =>
          // const role_perm = r_perm.filter(function (str) {
          //   return !str.includes("group_");
          // });
          !r_perm ? (
            "-"
          ) : (
            <div>
              {r_perm.map((s) =>
                !s.includes("group_") ? (
                  <Badge classNames="mb-1 mr-1" text={s} key={s} />
                ) : null
              )}
            </div>
          ),
        editComponent: (props) => <UpdateRoleReadPerm {...props} />,
      },
      {
        title: "Read Group",
        field: "r_perm",
        type: "string_array",
        tableType: "string",
        required: true,
        render: ({ r_perm }) =>
          !r_perm ? (
            "-"
          ) : (
            <div>
              {r_perm.map((s) =>
                s.includes("group_") ? (
                  <Badge
                    classNames="mb-1 mr-1"
                    text={s.replace("group_", "")}
                    key={s}
                  />
                ) : null
              )}
            </div>
          ),
        editComponent: (props) => <UpdateGroupReadPerm {...props} />,
      },
      {
        title: "Active",
        field: "active",
        type: "boolean",
        tableType: "boolean",
        required: true,
      },
    ],
    actions: [
      (rowData) => ({
        icon: tableIcons.ArrowTopRightOnSquare,
        onClick: () => {
          window.location.href = `/${localStorage.getItem(
            SELECTED_COUNTRY_KEY
          )}/service-configuration/${rowData.id}`;
        },
      }),
    ]
  },
  {
    path: "locale",
    name: "Translations",
    publicTable: true,
    columns: [
      // id,
      {
        title: "Key",
        field: "key",
        type: "string",
        tableType: "string",
        required: true,
      },
      {
        title: "Value",
        field: "value",
        type: "string",
        tableType: "string",
        required: true,
      },
      // {
      //   title: "Service",
      //   field: "service",
      //   type: "string",
      //   tableType: "string",
      //   required: true,
      // },
      {
        title: "Language",
        field: "locale",
        type: "string",
        tableType: "string",
        required: true,
      },
    ],
  },
  {
    path: "home-links",
    name: "Home links",
  },
  {
    path: "country_languages",
    name: "Active languages",
    publicTable: true,
    columns: [
      id,
      {
        title: "Name",
        field: "name",
        type: "string",
        tableType: "string",
        required: true,
      },
      {
        title: "Code",
        field: "code",
        type: "string",
        tableType: "string",
        required: true,
      },
      {
        title: "Locale",
        field: "locale",
        type: "string",
        tableType: "string",
        required: true,
      },
      {
        title: "Default",
        field: "default",
        type: "boolean",
        tableType: "boolean",
        required: true,
      },
    ],
  },
  {
    path: "public_links",
    name: "Public Links",
    editable: false,
    customAddOnClick: async (push, apiRequest, setData, setLoading) => {
      await UploadPublicLink(
        null,
        push,
        apiRequest,
        false,
        setData,
        setLoading
      );
    },
    getCustomDelete: GetDeletePublicLink,
    columns: [
      {
        title: "File",
        field: "filename",
        type: "string",
        tableType: "string",
        editComponent: (props) => {
          return (
            <input
              type="file"
              onChange={(e) => {
                props.onChange(e.target.files[0]);
              }}
            />
          );
        },
      },
      {
        title: "Link",
        field: "link",
        type: "string",
        filtering: false,
        render: (row, _rows, intl, lookup, setData) => (
          <ExplorerPublicLink
            row={row}
            _rows={_rows}
            intl={intl}
            lookup={lookup}
            setData={setData}
          />
        ),
      },
    ],
  },
  {
    path: "products",
    name: "Products",
  },
];

export default explorerTables;
